import React, { useState } from "react";
import { Typography, Card } from "antd";
import { FinancialChart } from "./FinancialChart";

const { Text } = Typography;

type ChartType = "area" | "bar" | "line";
type FilterType = "quarterly" | "annually";

interface ChartCardProps {
  title: string;
  subtitle?: string;
  graphData: any;
  loading: boolean;
  baseCurrency: string;
  defaultChartType?: ChartType;
  defaultFilter?: FilterType;
  onFilterChange?: (filter: FilterType) => void;
  showFilterToggle?: boolean;
}

const chartOptions: { label: string; value: ChartType }[] = [
  { label: "Area", value: "area" },
  { label: "Bars", value: "bar" },
  { label: "Line", value: "line" },
];

const filterOptions: { label: string; value: FilterType }[] = [
  { label: "Quarterly", value: "quarterly" },
  { label: "Annually", value: "annually" },
];

const ToggleGroup = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (val: T) => void;
}) => (
  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
    {options.map((opt, i) => (
      <React.Fragment key={opt.value}>
        <button
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 text-xs font-medium transition-colors ${
            value === opt.value
              ? "bg-gray-100 text-gray-900"
              : "bg-white text-gray-500 hover:bg-gray-50"
          }`}
        >
          {opt.label}
        </button>
        {i < options.length - 1 && <div className="w-px h-5 bg-gray-200" />}
      </React.Fragment>
    ))}
  </div>
);

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  graphData,
  loading,
  baseCurrency,
  defaultChartType = "area",
  defaultFilter = "annually",
  onFilterChange,
  showFilterToggle = true,
}) => {
  const [chartType, setChartType] = useState<ChartType>(defaultChartType);
  const [filter, setFilter] = useState<FilterType>(defaultFilter);

  const handleFilterChange = (val: FilterType) => {
    setFilter(val);
    onFilterChange?.(val);
  };

  return (
    <Card
      className="w-full shadow-sm border border-gray-200 rounded-xl"
      bodyStyle={{ padding: "16px" }}
    >
      {/* Card Header */}
      <div className="flex justify-between items-start mb-1">
        <div>
          <Text strong className="text-sm text-gray-800 block">
            {title}
          </Text>
          {subtitle && (
            <Text className="text-xs text-gray-400 uppercase tracking-wide">
              {subtitle}
            </Text>
          )}
        </div>

        {/* Toggles */}
        <div className="flex items-center gap-2">
          {showFilterToggle && (
            <ToggleGroup
              options={filterOptions}
              value={filter}
              onChange={handleFilterChange}
            />
          )}
          <ToggleGroup
            options={chartOptions}
            value={chartType}
            onChange={setChartType}
          />
        </div>
      </div>

      {/* Chart */}
      <FinancialChart
        graphData={graphData}
        baseCurrency={baseCurrency}
        loading={loading}
        absoluteChartType={chartType}
        percentageChartType={chartType}
        daysChartType={chartType}
        multiplesChartType={chartType}
      />
    </Card>
  );
};
